const ALLOWED_DRONE_PROTOCOLS = new Set(['http:', 'https:']);

export const MAX_DRONE_EXPORT_BASE64_LENGTH = 128 * 1024 * 1024;
const MAX_DATA_URI_HEADER_LENGTH = 128;
const MAX_METADATA_STRING_LENGTH = 4096;
const MAX_COORDINATE_STRING_LENGTH = 64;

const normalizeConfiguredUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null;

  try {
    const url = new URL(value.trim());
    if (!ALLOWED_DRONE_PROTOCOLS.has(url.protocol)) return null;
    if (url.username || url.password || url.search || url.hash) return null;
    return url.toString().replace(/\/$/, '');
  } catch (error) {
    return null;
  }
};

export const getDroneBackendUrl = () =>
  normalizeConfiguredUrl(process.env.REACT_APP_DRONE_BACKEND_URL);

export const getDroneFrontendUrl = () =>
  normalizeConfiguredUrl(process.env.REACT_APP_DRONE_FRONTEND_URL);

export const buildDroneShellUrl = (frontendUrl) => {
  const normalized = normalizeConfiguredUrl(frontendUrl);
  if (!normalized) return null;
  const url = new URL(normalized);
  url.pathname = `${url.pathname.replace(/\/+$/, '')}/`;
  return url;
};

export const buildDroneBootstrapMessage = ({
  companyId,
  projectId,
  leadId,
  leadName,
  leadType,
  step,
  latitude,
  longitude,
  address,
}) => ({
  type: 'dronmap:bootstrap',
  payload: {
    companyId,
    projectId,
    leadId,
    leadName,
    leadType,
    step,
    ...(Number.isFinite(latitude) ? { latitude } : {}),
    ...(Number.isFinite(longitude) ? { longitude } : {}),
    ...(address && typeof address === 'object' ? { address } : {}),
  },
});

export const postDroneBootstrap = (iframeWindow, frontendUrl, payload) => {
  const shellUrl = buildDroneShellUrl(frontendUrl);
  if (
    !iframeWindow?.postMessage ||
    !shellUrl ||
    !payload ||
    typeof payload !== 'object' ||
    Array.isArray(payload)
  ) {
    return false;
  }
  iframeWindow.postMessage(buildDroneBootstrapMessage(payload), shellUrl.origin);
  return true;
};

export const isDroneReadyMessage = (event, iframeWindow) => {
  const frontendUrl = getDroneFrontendUrl();
  if (!frontendUrl || event?.source !== iframeWindow) return false;
  const origin = new URL(frontendUrl).origin;
  if (event.origin !== origin) {
    return false;
  }
  return event.data?.type === 'dronmap:ready';
};

export const isDroneManagementConfigured = () =>
  Boolean(getDroneBackendUrl() && getDroneFrontendUrl());

const isPlainObject = (value) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const splitBase64Value = (value, allowedMimeTypes) => {
  if (typeof value !== 'string' || !value) return null;
  if (!value.startsWith('data:')) return value;

  const separator = value.indexOf(',');
  if (separator < 0 || separator > MAX_DATA_URI_HEADER_LENGTH) return null;

  const header = value.slice(5, separator).split(';');
  const mimeType = header.shift()?.toLowerCase();
  const encoding = header.pop()?.toLowerCase();
  if (!allowedMimeTypes.includes(mimeType) || encoding !== 'base64') return null;
  return value.slice(separator + 1);
};

const isStrictBase64 = (value) =>
  Boolean(value) &&
  value.length % 4 === 0 &&
  /^[A-Za-z0-9+/]+={0,2}$/.test(value);

const validateEncodedImage = (value, allowedMimeTypes, maxLength) => {
  if (typeof value !== 'string' || !value) return false;
  if (value.length > maxLength) return false;
  return isStrictBase64(splitBase64Value(value, allowedMimeTypes));
};

const OPTIONAL_SCALAR_FIELDS = [
  'projectId',
  'hostLeadId',
  'companyId',
  'projectName',
  'projectType',
  'leadType',
  'orthoTicketId',
  'sourcePlyTicketId',
  'rgbOutputId',
  'dsmOutputId',
];

const ADDRESS_FIELDS = ['street', 'city', 'state', 'country', 'zipcode', 'formatted'];

const isOptionalScalar = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.length <= MAX_METADATA_STRING_LENGTH) ||
  (typeof value === 'number' && Number.isFinite(value));

const isOptionalCoordinate = (value, minimum, maximum) => {
  if (value === undefined || value === null) return true;
  if (
    typeof value !== 'number' &&
    (typeof value !== 'string' ||
      !value.trim() ||
      value.length > MAX_COORDINATE_STRING_LENGTH)
  ) {
    return false;
  }
  const numericValue = Number(value);
  return (
    Number.isFinite(numericValue) &&
    numericValue >= minimum &&
    numericValue <= maximum
  );
};

export const validateDroneExportPayload = (
  payload,
  maxBase64Length = MAX_DRONE_EXPORT_BASE64_LENGTH
) => {
  if (!isPlainObject(payload)) {
    return { ok: false, error: 'Drone export payload is invalid.' };
  }

  if (
    !validateEncodedImage(payload.rgbPngBase64, ['image/png'], maxBase64Length) ||
    !validateEncodedImage(
      payload.dsmTiffBase64,
      ['image/tiff', 'image/tif', 'application/octet-stream'],
      maxBase64Length
    )
  ) {
    return { ok: false, error: 'Drone export images are missing or invalid.' };
  }

  if (OPTIONAL_SCALAR_FIELDS.some((field) => !isOptionalScalar(payload[field]))) {
    return { ok: false, error: 'Drone export metadata is invalid.' };
  }
  if (
    !isOptionalCoordinate(payload.lat, -90, 90) ||
    !isOptionalCoordinate(payload.long, -180, 180)
  ) {
    return { ok: false, error: 'Drone export coordinates are invalid.' };
  }
  if (payload.address !== undefined && !isPlainObject(payload.address)) {
    return { ok: false, error: 'Drone export address is invalid.' };
  }
  if (
    payload.address &&
    ADDRESS_FIELDS.some((field) => !isOptionalScalar(payload.address[field]))
  ) {
    return { ok: false, error: 'Drone export address is invalid.' };
  }

  const sanitizedPayload = {
    ...Object.fromEntries(
      OPTIONAL_SCALAR_FIELDS.filter((field) => payload[field] !== undefined).map(
        (field) => [field, payload[field]]
      )
    ),
    ...(payload.lat !== undefined ? { lat: payload.lat } : {}),
    ...(payload.long !== undefined ? { long: payload.long } : {}),
    ...(payload.address
      ? {
          address: Object.fromEntries(
            ADDRESS_FIELDS.filter((field) => payload.address[field] !== undefined).map(
              (field) => [field, payload.address[field]]
            )
          ),
        }
      : {}),
    rgbPngBase64: payload.rgbPngBase64,
    dsmTiffBase64: payload.dsmTiffBase64,
  };

  return { ok: true, payload: sanitizedPayload };
};

export const parseDroneExportMessage = (event, iframeWindow) => {
  if (event?.data?.type !== 'dronmap:export') {
    return { ok: false, ignored: true };
  }

  const frontendUrl = getDroneFrontendUrl();
  if (!frontendUrl || !iframeWindow) {
    return { ok: false, ignored: true };
  }

  const expectedOrigin = new URL(frontendUrl).origin;
  if (event.origin !== expectedOrigin || event.source !== iframeWindow) {
    return { ok: false, ignored: true };
  }

  return validateDroneExportPayload(event.data.payload);
};
